import type { Language, RoleResource, RoleResourceConstraint } from "@newgenerated/shared/schema";
import { delay } from "@utils/asyncUtils";
import type { HasAccessFn } from "@utils/widgetContextUtils";
import { getResourceConstraints } from "@newgenerated/shared/stubs/getResourceConstraints";
import { cannotHandleErrorHere } from "@utils/errorUtils";

function hasLanguage(constraint: RoleResourceConstraint, language?: Language): boolean {
  if (language === undefined || constraint.languages.length === 0) {
    return true;
  }
  return constraint.languages.includes(language);
}

function hasCorporation(constraint: RoleResourceConstraint, corporationId: number | undefined): boolean {
  if (corporationId === undefined || constraint.corporateIds.length === 0) {
    return true;
  }
  return constraint.corporateIds.includes(corporationId);
}

type RoleResourceConstraintState =
  | {
      type: "MISSING" | "LOADING";
    }
  | {
      type: "AVAILABLE";
      constraints: RoleResourceConstraint[];
    };

let state: RoleResourceConstraintState = { type: "MISSING" };

export async function setupRoleResourceConstraints(): Promise<RoleResourceConstraint[]> {
  while (true) {
    if (state.type === "MISSING") {
      // Load role resource constraints for the first time
      state = { type: "LOADING" };
      const result = await getResourceConstraints();
      cannotHandleErrorHere(result);
      const constraints = result.data.body;
      state = { type: "AVAILABLE", constraints };
      return constraints;
    }

    if (state.type === "AVAILABLE") {
      // Role resource constraints have already been set up
      return state.constraints;
    }

    // There is a request for the role resource constraints in process
    await delay(100);
  }
}

export async function hasAccess(resource: RoleResource, language?: Language, corporateId?: number): Promise<boolean> {
  const constraints = await setupRoleResourceConstraints();
  const constraint = constraints.find((roleResourceConstraint) => roleResourceConstraint.resource === resource);
  return constraint !== undefined && hasLanguage(constraint, language) && hasCorporation(constraint, corporateId);
}

export async function getCustomerIdFromConstraints(resource: RoleResource): Promise<number | null> {
  const constraints = await setupRoleResourceConstraints();
  const constraint = constraints.find((roleResourceConstraint) => roleResourceConstraint.resource === resource);
  return constraint?.customerId ?? null;
}

/*
 * Sync RoleResource handling
 * Should probably be deprecated soon, as it is possible to call the functions in the wrong order.
 */

function getConstraintsSync(): RoleResourceConstraint[] {
  return state.type === "AVAILABLE" ? state.constraints : [];
}

/**
 * Populates the same cache as <code>setupRoleResourceConstraints</code>, so that <code>hasAccessSync</code> can be used afterwards.
 * Safe to call multiple times / from multiple entry points on the same page - the underlying fetch only happens once.
 */
export async function fetchRoleResources(): Promise<void> {
  await setupRoleResourceConstraints();
}

/**
 * This prerequisites <code>fetchRoleResources()</code> to have been run before. Therefore it is safer to use <code>hasAccess</code> instead, as it makes sure that the role resource constraints have been fetched.
 */
export const hasAccessSync = (resource: RoleResource, language?: Language, corporateId?: number): boolean => {
  const constraint = getConstraintsSync().find((roleResourceConstraint) => roleResourceConstraint.resource === resource);
  return constraint !== undefined && hasLanguage(constraint, language) && hasCorporation(constraint, corporateId);
};

export const createHasAccessFunction = (fetchedConstraints: RoleResourceConstraint[]): HasAccessFn => {
  return (resource: RoleResource, language?: Language, corporateId?: number) => {
    const constraint = fetchedConstraints.find((roleResourceConstraint) => roleResourceConstraint.resource === resource);
    return constraint !== undefined && hasLanguage(constraint, language) && hasCorporation(constraint, corporateId);
  };
};

/**
 * This prerequisites <code>fetchRoleResources()</code> to have been run before. Therefore it is safer to use <code>getCustomerIdFromConstraints</code> instead, as it makes sure that the role resource constraints have been fetched.
 */
export const getCustomerIdFromConstraintsSync = (resource: RoleResource): number | null => {
  const constraint = getConstraintsSync().find((roleResourceConstraint) => roleResourceConstraint.resource === resource);
  return constraint?.customerId ?? null;
};

export class RoleService {
  constructor() {}

  //eslint-disable-next-line @typescript-eslint/no-explicit-any
  static install: any = (vue: any): void => {
    vue.prototype.$access = hasAccessSync;

    vue.prototype.$accessibleLanguagesOf = (resource: RoleResource, corporateId?: number): Language[] => {
      const constraint = getConstraintsSync().find((roleResourceConstraint) => roleResourceConstraint.resource === resource);

      if (constraint !== undefined && hasCorporation(constraint, corporateId)) {
        return constraint.languages;
      }
      return [];
    };
  };
}

// ts-ignore is necessary because of gaDesign. TODO: remove when gaVue is history
//eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
declare module "vue/types/vue" {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Vue {
    $access: (resource: RoleResource, language?: Language, corporateId?: number) => boolean;
    $accessibleLanguagesOf: (resource: RoleResource, corporateId?: number) => number[];
  }
}
